import React, { useEffect, useState } from 'react';
import { AlertCircle, BookOpen, CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import { JournalLine, nextJournalEntryId, postJournalEntry } from '../../../../../services/JournalEntriesService';
import { formatEuro } from '../masters/financeMasters';
import { FormSkeleton, SearchSelect, today } from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

const round2 = (n: number) => Math.round(n * 100) / 100;

interface EntryLine {
  id: string;
  account: string;
  debit: number;
  credit: number;
}

const newEntryLine = (): EntryLine => ({ id: String(Date.now() + Math.random()), account: '', debit: 0, credit: 0 });

interface ManualJournalFormProps {
  privacyMode: boolean;
  onDone?: () => void;
}

export const ManualJournalForm = ({ privacyMode, onDone }: ManualJournalFormProps) => {
  const { notify } = useToast();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [nextId, setNextId] = useState(0);

  const [date, setDate] = useState(today());
  const [concept, setConcept] = useState('');
  const [lines, setLines] = useState<EntryLine[]>([newEntryLine(), newEntryLine()]);

  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [chart, entryId] = await Promise.all([listChartOfAccounts(), nextJournalEntryId()]);
        if (cancelled) return;
        setAccounts(chart);
        setNextId(entryId);
        setLoadError('');
      } catch (err) {
        console.error('Error loading manual journal form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudo cargar el plan de cuentas.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Un apunte va al Debe o al Haber: escribir en uno vacía el otro
  const updateLine = (id: string, patch: Partial<EntryLine>) =>
    setLines(prev => prev.map(l => {
      if (l.id !== id) return l;
      const next = { ...l, ...patch };
      if (patch.debit) next.credit = 0;
      if (patch.credit) next.debit = 0;
      return next;
    }));

  const totalDebit = round2(lines.reduce((s, l) => s + l.debit, 0));
  const totalCredit = round2(lines.reduce((s, l) => s + l.credit, 0));
  const difference = round2(totalDebit - totalCredit);
  const balanced = totalDebit > 0 && difference === 0;
  const number = `AST-2026-${String(nextId).padStart(3, '0')}`;

  const accountName = (code: string) => {
    const account = accounts.find(a => a.code === code);
    return account ? account.name : '';
  };

  const validate = () => {
    if (!date) return 'La fecha es obligatoria.';
    if (!concept.trim()) return 'El concepto del asiento es obligatorio.';
    if (lines.some(l => !l.account)) return 'Todas las líneas necesitan una cuenta.';
    if (lines.some(l => l.debit <= 0 && l.credit <= 0)) return 'Todas las líneas necesitan un importe en el Debe o en el Haber.';
    if (!balanced) return 'El asiento no cuadra: el Debe y el Haber deben sumar lo mismo.';
    return '';
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(true);
    const journalLines: JournalLine[] = lines.map(l => ({
      account: l.account,
      accountName: accountName(l.account),
      debit: l.debit,
      credit: l.credit
    }));
    try {
      await postJournalEntry(concept.trim(), journalLines, 'Diario Manual', accounts, date);
    } catch (err) {
      console.error('Error posting manual journal entry:', err);
      setFormError(`No se pudo contabilizar el asiento: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setSaving(false);
      return;
    }
    notify('Asiento contabilizado', `Asiento "${concept.trim()}" de ${formatEuro(totalDebit, privacyMode)} contabilizado en el Libro Diario.`, 'info');
    setSaving(false);
    if (onDone) onDone();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const accountOptions = accounts
    .filter(a => a.level === 'account' || a.level === 'subaccount' || (!a.level && a.code.length >= 3))
    .map(a => ({ value: a.code, label: `${a.code} · ${a.name}` }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">CONTABILIDAD • ASIENTOS MANUALES</span>
          <h3 className="fin-doc__title"><BookOpen size={14} /> Asiento manual</h3>
        </div>
        <span className="fin-doc__badge">DOBLE ENTRADA</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Nº asiento autonumérico</label>
          <input
            type="text"
            readOnly
            className="sidebar-form-input fin-doc__mono fin-doc__readonly"
            value={`${number} (Automático)`}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Concepto *</label>
          <input
            type="text"
            className="sidebar-form-input"
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            placeholder="Ej. Regularización o ajuste manual"
          />
        </div>
      </div>

      <div className="fin-lines">
        <div className="fin-lines__header">
          <span>Apuntes del asiento</span>
          <button type="button" className="fin-lines__add" onClick={() => setLines([...lines, newEntryLine()])}>
            <Plus size={12} /> AÑADIR LÍNEA
          </button>
        </div>

        <div className="fin-lines__row fin-lines__row--head fin-lines__row--journal">
          <span>Cuenta del Plan de Cuentas</span>
          <span className="right">Debe (€)</span>
          <span className="right">Haber (€)</span>
          <span />
        </div>

        {lines.map(line => (
          <div key={line.id} className="fin-lines__row fin-lines__row--journal">
            <SearchSelect
              options={accountOptions}
              value={line.account}
              onChange={(account) => updateLine(line.id, { account })}
              placeholder="Buscar cuenta por código o nombre (muestra solo 50 resultados)..."
              emptyText="No hay cuentas"
            />
            <input
              type="number"
              min={0}
              step="0.01"
              className="sidebar-form-input right"
              value={line.debit}
              onChange={(e) => updateLine(line.id, { debit: parseFloat(e.target.value) || 0 })}
            />
            <input
              type="number"
              min={0}
              step="0.01"
              className="sidebar-form-input right"
              value={line.credit}
              onChange={(e) => updateLine(line.id, { credit: parseFloat(e.target.value) || 0 })}
            />
            <button
              type="button"
              className="fin-lines__remove"
              title="Eliminar línea"
              disabled={lines.length <= 2}
              onClick={() => setLines(lines.filter(l => l.id !== line.id))}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      <div className="fin-doc__totals">
        <span>Total Debe: <strong>{formatEuro(totalDebit, privacyMode)}</strong></span>
        <span>Total Haber: <strong>{formatEuro(totalCredit, privacyMode)}</strong></span>
      </div>

      <div className={`fin-doc__balance ${balanced ? 'ok' : 'ko'}`}>
        {balanced ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
        {balanced
          ? 'El asiento cuadra: Debe = Haber.'
          : totalDebit === 0 && totalCredit === 0
            ? 'Introduzca los importes del Debe y del Haber.'
            : `Descuadre: ${formatEuro(Math.abs(difference), privacyMode)} de más en el ${difference > 0 ? 'Debe' : 'Haber'}.`}
      </div>

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn" disabled title="Pendiente de endpoint">
          Guardar borrador
        </button>
        <button
          type="button"
          className="fin-doc__btn fin-doc__btn--primary"
          disabled={!balanced || saving}
          title={balanced ? undefined : 'Se activa cuando el asiento cuadra'}
          onClick={handleSubmit}
        >
          {saving ? 'Contabilizando...' : 'Contabilizar'}
        </button>
      </div>
    </div>
  );
};

export default ManualJournalForm;
